import React, {lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled, {useTheme} from 'styled-components';
import {RouteComponentProps, useLocation} from 'react-router-dom';
import Layout from './Layout';
import StyledContainer from '../components/Container';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import {getThemedSelectMenuProps} from 'utils/muiMenuProps';
import ThemedSelectMenuItem from 'components/MUI/ThemedSelectMenuItem';
import Skeleton from '@material-ui/lab/Skeleton';
import {useOktaAuth} from '@okta/okta-react';
import config from '../oktaConfig';
import SGTitle from 'components/Typography/SGTitle';
import SGBody from 'components/Typography/SGBody';
import SGHeadline from 'components/Typography/SGHeadline';
import NavCrumb from 'components/Navigation/NavCrumb';
import Paper from 'components/Paper';
import useUserLogs from 'hooks/useUserLogs';
import {addJsonSheet, createWorkbook, downloadWorkbook} from 'utils/excelExport';
import Icon from '@mdi/react';
import {mdiDownloadOutline} from '@mdi/js';
import {flattenRecordsForExcel} from 'utils/flattenForExcel';
import {
  findStoresCountryColumnId,
  pickStoresTableDisplayColumns,
} from 'utils/storesTableDisplayColumns';
import {getStoresTableColumnSizing} from 'utils/storesColumnSizing';
import {humanizeColumnKey} from 'utils/humanizeColumnKey';
import {getStoresExcelColumnOrder} from 'utils/storesExcelColumns';

const Table = lazy(() => import('components/Tables/TableStructure/Table'));

const SELECTED_REGION_CODE: Record<string, string> = {
  'North America': 'NA',
  EMEA: 'EMEA',
  APLA: 'APLA',
  'Greater China': 'GC',
};

/** First option loads all regions (GET /Stores with no `geo` query). */
const REGION_OPTION_ALL = 'All' as const;

const REGIONS_ALL = [REGION_OPTION_ALL, 'North America', 'EMEA', 'APLA', 'Greater China'] as const;

/** Omit `brand` query when "All"; otherwise `brand=Nike` or `brand=Converse`. */
const BRAND_OPTION_ALL = 'All' as const;
const BRANDS_ALL = [BRAND_OPTION_ALL, 'Nike', 'Converse'] as const;

/** Client-side filter; options are derived from the current API response. */
const COUNTRY_OPTION_ALL = 'All' as const;

const customRowsPerPage = {
  defaultRowsPerPage: 20,
  rowsPerPageOptions: [20, 50, 100, {label: 'All', value: -1}],
};

/** MUI table row `id` / React key — prefer API fields so keys stay stable when the list changes. */
function getStoresTableRowId(row: Record<string, string>, rowIndex: number): string {
  const siteId = row.SiteId?.trim();
  if (siteId) {
    return `store-site-${siteId}`;
  }
  const storeNumber = row.StoreNumber?.trim();
  if (storeNumber) {
    return `store-num-${storeNumber}`;
  }
  return `stores-row-${rowIndex}`;
}

const StyledTableContainer = styled.div`
  border: 1px solid ${props => props.theme.text};
  border-radius: 10px;
  padding: 8px 16px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background-color: ${props => props.theme.cardBg};
  /* Horizontal scroll lives on the inner MUI TableContainer; avoid nested scroll + width gaps */
  overflow-x: hidden;
  .MuiTableBody-root .MuiTableCell-body {
    padding-top: 12px;
    padding-bottom: 12px;
  }
`;

const StyledSkeleton = styled(Skeleton)`
  height: 300px;
  background-color: ${props => props.theme.text}0D;
`;

const ExportButton = styled(Button)`
  background-color: ${props => props.theme.exportButtonBg || props.theme.activeStateBlue};
  text-transform: capitalize;
  font-family: 'Trade Gothic Bold Condensed';
  border-radius: 10px;
  box-sizing: border-box;
  min-height: 56px;
  height: 56px;
  padding: 0 16px;
  line-height: 1;
  color: ${props => props.theme.defaultNavy};
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  .MuiButton-startIcon {
    margin-right: 8px;
  }
  &:hover {
    background-color: ${props => `${props.theme.exportButtonBg || props.theme.activeStateBlue}cc`};
  }
  &.MuiButton-root.Mui-disabled {
    background-color: ${props => props.theme.grey}80;
    color: ${props => props.theme.defaultNavy};
  }
`;

const ExportModalOverlay = styled(Modal)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ExportModalContent = styled(Paper)`
  max-width: 500px;
  width: min(92vw, 500px);
  padding: 2rem;
  position: relative;
  outline: none;
`;

const ExportModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ExportModalTitle = styled(SGTitle)`
  margin: 0;
`;

const ExportModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0;
  text-align: center;
`;

const ExportProgressSpinner = styled(CircularProgress)`
  color: ${props => props.theme.text};
`;

const RegionSelector = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 16px;

  .MuiFormControl-root,
  .MuiInputBase-root {
    color: ${props => props.theme.text};
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${props => props.theme.text};
  }

  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${props => props.theme.activeStateBlue};
  }

  .MuiSelect-icon {
    color: ${props => props.theme.text};
  }
`;

function Stores({match}: RouteComponentProps) {
  const theme = useTheme();
  const themedSelectMenuProps = useMemo(() => getThemedSelectMenuProps(theme), [theme]);
  const location = useLocation();
  const crumbMatch = {url: location?.pathname || '/stores'};
  const {oktaAuth} = useOktaAuth();
  const {dispatch: dispatchUserLog} = useUserLogs();

  const [selectedRegion, setSelectedRegion] = useState<string>('North America');
  const [selectedCountry, setSelectedCountry] = useState<string>(COUNTRY_OPTION_ALL);
  const [selectedBrand, setSelectedBrand] = useState<string>(BRAND_OPTION_ALL);
  const [rawRows, setRawRows] = useState<unknown[]>([]);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const isMountedRef = useRef(true);
  const exportDeferTimeoutRef = useRef<number | null>(null);
  const exportCloseTimeoutRef = useRef<number | null>(null);

  const clearExportTimeouts = useCallback(() => {
    if (exportDeferTimeoutRef.current != null) {
      window.clearTimeout(exportDeferTimeoutRef.current);
      exportDeferTimeoutRef.current = null;
    }
    if (exportCloseTimeoutRef.current != null) {
      window.clearTimeout(exportCloseTimeoutRef.current);
      exportCloseTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearExportTimeouts();
    };
  }, [clearExportTimeouts]);

  /** Geo query for API; `undefined` when "All" is selected (omit `geo`). */
  const geoCode =
    selectedRegion === REGION_OPTION_ALL ? undefined : SELECTED_REGION_CODE[selectedRegion];

  /** Brand query for API; `undefined` when "All" is selected (omit `brand`). */
  const brandParam = selectedBrand === BRAND_OPTION_ALL ? undefined : selectedBrand;

  const storesDataUrl = useMemo(() => {
    const base = config.resourceServer.insightsApiUrlStoresData;
    const params = new URLSearchParams();
    if (geoCode !== undefined) {
      params.set('geo', geoCode);
    }
    if (brandParam !== undefined) {
      params.set('brand', brandParam);
    }
    const q = params.toString();
    return q ? `${base}?${q}` : base;
  }, [geoCode, brandParam]);

  const {flatRows, columnOrder} = useMemo(() => flattenRecordsForExcel(rawRows), [rawRows]);

  const countryColumnId = useMemo(() => findStoresCountryColumnId(columnOrder), [columnOrder]);

  const countryOptions = useMemo(() => {
    if (!countryColumnId) {
      return [COUNTRY_OPTION_ALL];
    }
    const unique = new Set<string>();
    flatRows.forEach(row => {
      const v = row[countryColumnId]?.trim();
      if (v) {
        unique.add(v);
      }
    });
    return [COUNTRY_OPTION_ALL, ...Array.from(unique).sort((a, b) => a.localeCompare(b))];
  }, [flatRows, countryColumnId]);

  useEffect(() => {
    if (selectedCountry === COUNTRY_OPTION_ALL) {
      return;
    }
    if (!countryOptions.includes(selectedCountry)) {
      setSelectedCountry(COUNTRY_OPTION_ALL);
    }
  }, [countryOptions, selectedCountry]);

  const filteredFlatRows = useMemo(() => {
    if (selectedCountry === COUNTRY_OPTION_ALL || !countryColumnId) {
      return flatRows;
    }
    return flatRows.filter(row => (row[countryColumnId]?.trim() ?? '') === selectedCountry);
  }, [flatRows, countryColumnId, selectedCountry]);

  /** Excel omits store-hours columns; on-screen table uses a separate summary subset. */
  const excelExportColumnOrder = useMemo(() => getStoresExcelColumnOrder(columnOrder), [
    columnOrder,
  ]);

  /** Subset for on-screen table only */
  const displayColumns = useMemo(() => pickStoresTableDisplayColumns(columnOrder), [columnOrder]);

  const columns = useMemo(
    () =>
      displayColumns.map(col => ({
        id: col.id,
        label: col.label,
        ...getStoresTableColumnSizing(col),
      })),
    [displayColumns]
  );

  const tableData = useMemo(
    () =>
      filteredFlatRows.map((row, idx) => ({
        ...row,
        id: getStoresTableRowId(row, idx),
      })),
    [filteredFlatRows]
  );

  useEffect(() => {
    dispatchUserLog('Stores: page view');
  }, [dispatchUserLog]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    async function load() {
      setPending(true);
      setError(null);
      const accessToken = oktaAuth.getAccessToken();
      if (!accessToken) {
        setPending(false);
        return;
      }
      try {
        const response = await fetch(storesDataUrl, {
          mode: 'cors',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`);
        }
        const data = await response.json();
        if (!cancelled) {
          setRawRows(Array.isArray(data) ? data : []);
        }
      } catch (e: unknown) {
        if (!cancelled && (e as Error)?.name !== 'AbortError') {
          setError((e as Error)?.message || 'Failed to load stores');
          setRawRows([]);
        }
      } finally {
        if (!cancelled) {
          setPending(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [oktaAuth, storesDataUrl]);

  const handleRegionChange = (
    e: React.ChangeEvent<{name?: string; value: unknown}>,
    _child: React.ReactNode
  ) => {
    const region = String(e.target.value);
    setSelectedRegion(region);
    setSelectedCountry(COUNTRY_OPTION_ALL);
    dispatchUserLog('Stores: region change', {
      regionLabel: region,
      geo: region === REGION_OPTION_ALL ? undefined : SELECTED_REGION_CODE[region],
    });
  };

  const handleCountryChange = (
    e: React.ChangeEvent<{name?: string; value: unknown}>,
    _child: React.ReactNode
  ) => {
    const country = String(e.target.value);
    setSelectedCountry(country);
    dispatchUserLog('Stores: country change', {
      country: country === COUNTRY_OPTION_ALL ? undefined : country,
    });
  };

  const handleBrandChange = (
    e: React.ChangeEvent<{name?: string; value: unknown}>,
    _child: React.ReactNode
  ) => {
    const brand = String(e.target.value);
    setSelectedBrand(brand);
    dispatchUserLog('Stores: brand change', {
      brand: brand === BRAND_OPTION_ALL ? undefined : brand,
    });
  };

  const handleExportExcel = useCallback(() => {
    if (!filteredFlatRows.length) {
      return;
    }
    clearExportTimeouts();
    setExportModalOpen(true);
    const openedAt = Date.now();
    /** Lets the dialog paint before Excel export work blocks the main thread */
    const MIN_DIALOG_MS = 450;

    exportDeferTimeoutRef.current = window.setTimeout(async () => {
      exportDeferTimeoutRef.current = null;
      try {
        const date = new Date().toISOString().slice(0, 10);
        const countrySegment =
          selectedCountry === COUNTRY_OPTION_ALL ? 'All_Countries' : selectedCountry;
        const filename = `Stores_${geoCode ?? 'All_Regions'}_${countrySegment}_${
          brandParam ?? 'All_Brands'
        }_${date}.xlsx`;
        const excelHeaderLabels = excelExportColumnOrder.map(col => humanizeColumnKey(col));
        const sheetData = filteredFlatRows.map(row => {
          const line: Record<string, string> = {};
          excelExportColumnOrder.forEach((col, i) => {
            line[excelHeaderLabels[i]] = row[col] ?? '';
          });
          return line;
        });
        const workbook = createWorkbook();
        addJsonSheet(workbook, 'Stores', sheetData);
        await downloadWorkbook(workbook, filename);
        dispatchUserLog('Stores: export excel', {
          geo: geoCode,
          regionLabel: selectedRegion,
          country: selectedCountry === COUNTRY_OPTION_ALL ? undefined : selectedCountry,
          brand: brandParam,
          rowCount: filteredFlatRows.length,
        });
      } finally {
        if (!isMountedRef.current) {
          return;
        }
        const wait = Math.max(0, MIN_DIALOG_MS - (Date.now() - openedAt));
        exportCloseTimeoutRef.current = window.setTimeout(() => {
          exportCloseTimeoutRef.current = null;
          if (isMountedRef.current) {
            setExportModalOpen(false);
          }
        }, wait);
      }
    }, 0);
  }, [
    filteredFlatRows,
    excelExportColumnOrder,
    geoCode,
    brandParam,
    selectedRegion,
    selectedCountry,
    dispatchUserLog,
    clearExportTimeouts,
  ]);

  return (
    <Layout match={match}>
      <ExportModalOverlay
        open={exportModalOpen}
        onClose={() => {}}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        disableEscapeKeyDown
        aria-labelledby="stores-export-modal-title"
        aria-describedby="stores-export-modal-description"
      >
        <Fade in={exportModalOpen}>
          <ExportModalContent>
            <ExportModalHeader>
              <span id="stores-export-modal-title">
                <ExportModalTitle className="active-color">Exporting to Excel</ExportModalTitle>
              </span>
            </ExportModalHeader>
            <ExportModalBody id="stores-export-modal-description">
              <ExportProgressSpinner size={36} />
              <SGBody variant="b4" className="text-color">
                Preparing your file. Large exports can take a little while; please keep this page
                open until the download starts.
              </SGBody>
            </ExportModalBody>
          </ExportModalContent>
        </Fade>
      </ExportModalOverlay>
      <StyledContainer>
        <div style={{marginBottom: '1rem', marginTop: '-0.5rem', fontSize: '1rem'}}>
          <NavCrumb match={crumbMatch} breadCrumb className="stores-breadcrumb" />
        </div>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SGHeadline className="text-color" variant="h5">
              Stores
            </SGHeadline>
            <SGBody variant="b4" className="text-color">
              Browse retail store information by region, country, and brand, and export the current
              list to Excel.
            </SGBody>
          </Grid>
          <Grid item xs={12}>
            {error ? (
              <Paper>
                <SGBody className="text-color">{error}</SGBody>
              </Paper>
            ) : (
              <Paper>
                <RegionSelector>
                  <div>
                    <SGTitle className="active-color">Select Region</SGTitle>
                    <FormControl variant="outlined" disabled={pending}>
                      <Select
                        autoWidth
                        value={selectedRegion}
                        onChange={handleRegionChange}
                        label="Select-Region"
                        inputProps={{'aria-label': 'Select region'}}
                        MenuProps={themedSelectMenuProps}
                      >
                        {REGIONS_ALL.map(val => (
                          <ThemedSelectMenuItem key={val} value={val}>
                            {val}
                          </ThemedSelectMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div>
                    <SGTitle className="active-color">Select Country</SGTitle>
                    <FormControl variant="outlined" disabled={pending}>
                      <Select
                        autoWidth
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        label="Select-Country"
                        inputProps={{'aria-label': 'Select country'}}
                        MenuProps={themedSelectMenuProps}
                      >
                        {countryOptions.map(val => (
                          <ThemedSelectMenuItem key={val} value={val}>
                            {val}
                          </ThemedSelectMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div>
                    <SGTitle className="active-color">Select Brand</SGTitle>
                    <FormControl variant="outlined" disabled={pending}>
                      <Select
                        autoWidth
                        value={selectedBrand}
                        onChange={handleBrandChange}
                        label="Select-Brand"
                        inputProps={{'aria-label': 'Select brand'}}
                        MenuProps={themedSelectMenuProps}
                      >
                        {BRANDS_ALL.map(val => (
                          <ThemedSelectMenuItem key={val} value={val}>
                            {val}
                          </ThemedSelectMenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <ExportButton
                    startIcon={<Icon path={mdiDownloadOutline} size={0.8} />}
                    onClick={handleExportExcel}
                    disabled={pending || filteredFlatRows.length === 0}
                  >
                    Export to Excel
                  </ExportButton>
                </RegionSelector>
                <StyledTableContainer>
                  <Suspense fallback={<StyledSkeleton variant="rect" animation="wave" />}>
                    <Table
                      key={`${geoCode ?? 'all-regions'}-${selectedCountry}-${selectedBrand}`}
                      fillScrollArea
                      tableLayout="auto"
                      paginationText="Stores"
                      customRowsPerPage={customRowsPerPage}
                      columns={columns}
                      data={tableData}
                      noDataText="No stores returned for the selected filters."
                      loaded={!pending}
                      orderByCol={columns[0]?.id ?? 'id'}
                    />
                  </Suspense>
                </StyledTableContainer>
              </Paper>
            )}
          </Grid>
        </Grid>
      </StyledContainer>
    </Layout>
  );
}

export default Stores;
